import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import {
    catchError,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { WorkflowStateCellRendererParams } from './models/workflow-state-cell-renderer-params';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { WorkflowStateFormModel } from './models/workflow-state-form-model';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { WorkflowState } from '../../../../../../modules/service-fulfillment/constants/workflow-state';
import { ServiceFulfillmentService } from '../../../../../../modules/service-fulfillment/services/service-fulfillment.service';
import { UpdateWorkflowStateRequest } from '../../../../../../modules/service-fulfillment/models/update-workflow-state-request';

@Component({
    selector: 'app-workflow-state-cell-renderer',
    imports: [MatSelectModule, MatFormFieldModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './workflow-state-cell-renderer.component.html',
})
export class WorkflowStateCellRendererComponent
    implements ICellRendererAngularComp, OnDestroy
{
    private destroy$ = new Subject<void>();

    params!: WorkflowStateCellRendererParams;

    workflowStateDropdownOptions: DropdownOption[] = [];

    form: FormGroup<FormGroupType<WorkflowStateFormModel>> =
        this.getFormDefinition();

    constructor(
        private readonly serviceFulfillmentService: ServiceFulfillmentService,
    ) {}

    agInit(params: WorkflowStateCellRendererParams): void {
        this.params = params;

        this.bindForm(params);
        this.setupFormListeners();
        this.setupDropdownOptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): boolean {
        return false;
    }

    private bindForm(params: WorkflowStateCellRendererParams): void {
        this.form.patchValue({
            workflowState: params.data.workflowState,
        });

        // Disable the dropdown if the value is already Completed
        if (params.data.workflowState === WorkflowState.Completed) {
            this.form.controls.workflowState.disable();
        }
    }

    private getFormDefinition(): FormGroup<
        FormGroupType<WorkflowStateFormModel>
    > {
        return new FormGroup<FormGroupType<WorkflowStateFormModel>>({
            workflowState: new FormControl<WorkflowState | null>(
                null,
                Validators.required,
            ),
        });
    }

    private saveFormDataToApi(): Observable<void> {
        const request: UpdateWorkflowStateRequest = {
            applicationId: this.params.data.applicationId,
            workflowState: this.form.controls.workflowState.value!,
        };
        return this.serviceFulfillmentService.updateApplicationWorkflowState(
            request,
        );
    }

    private setupFormListeners(): void {
        const workflowStateControl = this.form.controls.workflowState;
        let previousWorkflowState: WorkflowState | null =
            workflowStateControl.value;

        this.form.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.saveFormDataToApi()),
                tap(() => {
                    const currentWorkflowState = workflowStateControl.value!;
                    this.updateWorkflowStateAndRefreshGrid(
                        currentWorkflowState,
                    );
                    previousWorkflowState = currentWorkflowState;
                }),
                catchError(() => {
                    if (previousWorkflowState !== null) {
                        workflowStateControl.setValue(previousWorkflowState, {
                            emitEvent: false,
                        });
                    }
                    return of(void 0);
                }),
            )
            .subscribe();
    }

    /**
     * Updates the workflow state value for the current grid row
     * and refreshes the grid to reflect the change.
     *
     * @param workflowState - The new workflow state to set
     */
    private updateWorkflowStateAndRefreshGrid(
        workflowState: WorkflowState,
    ): void {
        this.params.data.workflowState = workflowState;

        // Refresh the specific cell to reflect the updated status
        this.params.api.refreshCells({
            rowNodes: [this.params.node],
            force: true,
        });

        // Notify the grid that a filter change may have occurred
        this.params.api.onFilterChanged();
    }

    private setupDropdownOptions(): void {
        this.workflowStateDropdownOptions = this.params.dropdownOptions;
    }
}
